// ============================================================================
// JARVISTRAVEL - MAIN APPLICATION
// Version: 1.0.0
// ============================================================================

import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef, lazy, Suspense, startTransition } from "react";
import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import type { ComponentType, ReactNode } from "react";
import { LoaderScreen, SignInScreen, SignUpScreen, ForgotPasswordScreen, VerifyEmailScreen, EarlyAccessNotice, SetupMfaScreen } from "@/app/components/auth";
import { AUTH_PREFIX, FORGOT_PASSWORD_PATH, SIGN_IN_PATH, SIGN_UP_PATH, VERIFY_EMAIL_PATH } from "@/app/components/auth/authPaths";
import { SETUP_MFA_PATH, taskPath } from "@/app/components/auth/sessionTask";
import { SignInUnreachable } from "@/app/components/auth/SignInUnreachable";
import { useClerkStalled } from "@/app/hooks/useClerkStalled";
import type { User, JournalTrip } from "@/app/types";
import { useUser, useAuth, useSession } from "@clerk/clerk-react";
import { setDemoSession, clearDemoSession, isDemoSession } from "@/app/api";
import { setPendingTour } from "@/app/api/onboardingTour";
import { getLocalTrips, discardLegacyLocalTrips, type TripsOwner } from "@/app/data/localTrips";
import { listTrips } from "@/app/api/trips";
import { readOnboardedOnCore, recordOnboardedOnCore } from "@/app/api/onboarding";
import { reportApiFailure } from "@/app/api/failure";
import { syncNameToClerk } from "@/app/api/auth";
import { discardLegacySettings } from "@/app/data/localSettings";
import { demoSessionId } from "@/app/data/demoIdentity";
import { purgeAllLocalUserData, purgeLegacyAuthKeys } from "@/app/data/localDataPurge";
import { purgeDemoData } from "@/app/data/demoDataPurge";
import { capturePlanIntent, readPlanIntent } from "@/app/utils/planIntent";
import { CHECKOUT_RETURN_PATHS, SETTINGS_PATH } from "@/app/api/payments";
import { CheckoutReturn } from "@/app/components/checkout/CheckoutReturn";
import { InvitationLink } from "@/app/components/invitations/InvitationLink";

import { ErrorBoundary, RouteErrorFallback, Toaster, toast, BackendUnavailableBanner, UpdateReadyBanner } from "@/app/components/shared";
import { AddToHomeScreenPrompt } from "@/app/components/AddToHomeScreenPrompt";
import { captureException } from "@/app/utils/observability";
import { consumeReturnTo, saveReturnTo } from "@/app/utils/returnTo";
import { BELL_PATH, OPEN_BELL_MESSAGE, resaveThisDevice, turnOffPush } from "@/app/utils/webPush";
import { INVITATION_PATHS, isForNewAddress, isInvitationPath } from "@/app/utils/invitationLinks";
import { isSelfSignupEnabled } from "@/app/utils/earlyAccess";
import { usePageViewTracking } from "@/app/hooks/usePageViewTracking";
import { SHARED_JOURNAL_PREFIX, SHARED_JOURNAL_ROUTE } from "@/app/constants";
import { useScrollRestoration } from "@/app/hooks/useScrollRestoration";
import { useTripLockedToast } from "@/app/hooks/useTripLockedToast";
import { trackSignUp, trackLogin, trackLogout, trackTripCreated, identifyUser, resetAnalytics } from "@/app/utils/analytics";

// JAR-2464: the post-login "Add to Home Screen" nudge is off for launch. The
// installed PWA is not good enough yet, so nobody is sent to install it until
// JAR-2463's audit decides it should return. This is the one switch; set it to
// true to bring the nudge back. Typed as boolean (not the literal false) so the
// mount below stays type-checked rather than reading as dead code.
const SHOW_ADD_TO_HOME_SCREEN_NUDGE: boolean = false;

// Maps pull in MapLibre (heavy) — load each map route only when visited, and from
// its own module so one screen doesn't drag in the others' code (no barrel).
// Split off the authenticated app (JAR-1359).
//
// /auth/sign-in is the first screen every user sees and the one a marketing
// click lands on, and it was shipping the whole application: 422 KiB of the
// 704 KiB it downloaded was JavaScript that screen never runs. FCP 3.0 s and
// LCP 3.6 s, with Total Blocking Time at 10 ms — so the time was not the app
// working, it was the browser fetching and parsing a dashboard nobody had
// signed in to yet.
//
// These three are reachable only AFTER authentication (or, for the Plaid
// return, only as a redirect back into an authenticated session), so nothing
// on the sign-in path needs them present.
//
// Dashboard is the big one: it pulls the tabs, the concierge and the budget
// tree behind it. The map screens below were already split; this is the same
// treatment applied to the half that was missed.
const Dashboard = lazy(() => import("@/app/components/dashboard").then((m) => ({ default: m.Dashboard })));
const OnboardingFlow = lazy(() => import("@/app/components/onboarding").then((m) => ({ default: m.OnboardingFlow })));
const PlaidOAuthReturn = lazy(() => import("@/app/components/budget/PlaidOAuthReturn").then((m) => ({ default: m.PlaidOAuthReturn })));

const TripOverviewMapScreen = lazy(() => import("@/app/components/maps/screens/TripOverviewMapScreen"));
const NearbySearchMapScreen = lazy(() => import("@/app/components/maps/screens/NearbySearchMapScreen"));
const ItineraryItemMapScreen = lazy(() => import("@/app/components/maps/screens/ItineraryItemMapScreen"));
const ProgressiveFlowMapScreen = lazy(() => import("@/app/components/maps/screens/ProgressiveFlowMapScreen"));
const JournalMemoryMapScreen = lazy(() => import("@/app/components/maps/screens/JournalMemoryMapScreen"));

const TripJournalStory = lazy(() =>
  import("@/app/components/journal").then((m) => ({ default: m.TripJournalStory }))
);
// The public /j/:token page. Its own module so a recipient's bundle does not
// carry the owner's journal screen unless a sample trip is actually shown.
const SharedJournalRoute = lazy(() =>
  import("@/app/components/journal/SharedJournalRoute").then((m) => ({ default: m.SharedJournalRoute }))
);

const mapFallback = <div className="fixed inset-0 flex items-center justify-center text-muted">Loading map…</div>;

// The authenticated app's loading state (JAR-1359).
//
// NOT LoaderScreen, which is what I reached for first: it takes a required
// onComplete and drives a timed brand animation that then calls back. A
// Suspense fallback is unmounted the moment the chunk resolves, so that
// callback may never fire — and on a warm cache the chunk arrives in
// milliseconds, which would flash a branded intro at someone who is already
// signed in. Quiet and short-lived is the right shape here.
//
// Same treatment as mapFallback above, so the app has one loading idiom.
const appFallback = (
  <div className="fixed inset-0 flex items-center justify-center text-muted" role="status" aria-live="polite">
    Loading…
  </div>
);

// Onboarding completion survives a refresh (a new user who reloads mid-flow
// must land back in onboarding, not on an empty dashboard). A user with trips,
// in this browser or on core, has clearly finished onboarding — that also
// migrates anyone from before this flag existed.
const ONBOARDED_KEY = "jarvis_onboarded";
// Takes the owner explicitly. It used to call getLocalTrips() with whatever the
// module happened to hold, which on an early call was nothing — so the trips
// branch silently answered "no trips" rather than "I was asked before anyone
// was signed in". Now the caller has to say (JAR-704).
function readOnboarded(owner: TripsOwner): boolean {
  try {
    if (localStorage.getItem(ONBOARDED_KEY) === "1") return true;
    return getLocalTrips(owner).length > 0;
  } catch {
    return false;
  }
}
// When this browser says "not onboarded", core is asked: signing out clears the
// flag and the browser's trips (JAR-1756). Core keeps it per account (JAR-1870),
// so deleting every trip doesn't undo it; an account that onboarded before then
// has trips on core to show for it instead. Never for a demo, which has no
// account. A read that fails answers "unknown", not "no": sending someone who
// has onboarded back through it because core could not be read is the very
// thing this asks core to prevent (Ryan's review, web-app#470).
async function onboardedOnCore(owner: TripsOwner): Promise<boolean | "unknown"> {
  if (!owner || isDemoSession()) return false;
  try {
    return (await readOnboardedOnCore()) || (await listTrips()).length > 0;
  } catch {
    return "unknown";
  }
}
// The one onboarding decision: this browser first, then core. The browser's yes
// comes back at once rather than as a promise, so routing that doesn't need core
// never waits a tick for it: an awaited yes lost a race with the loader. A yes
// from core is written down by the caller, once it has checked the answer is
// still for the person signed in.
function readOnboardedAnywhere(owner: TripsOwner): true | Promise<boolean | "unknown"> {
  return readOnboarded(owner) || onboardedOnCore(owner);
}
function writeOnboarded(v: boolean): void {
  try {
    if (v) localStorage.setItem(ONBOARDED_KEY, "1");
    else localStorage.removeItem(ONBOARDED_KEY);
  } catch { /* storage unavailable — session state still works */ }
}
// Onboarding is done: this browser remembers it at once, and core keeps it for
// the account (JAR-1870). Only for the account that finished it, or that core
// says finished it: the browser's flag is not per account, so it may be another
// person's. Recording a yes that came from trips on core moves an account from
// before then onto the flag. With nobody signed in there is no account to keep
// it for, and the browser's flag would pass to whoever signs in next.
function markOnboarded(owner: TripsOwner): void {
  if (!owner) return;
  writeOnboarded(true);
  recordOnCore(owner);
}
// A core write that fails is reported and retried, from a marker kept per
// account, at most once a page load: a core that keeps refusing costs one
// request and one report a load, not one a navigation. One write at a time per
// account. Never on core for a demo.
const pendingOnCore = (owner: string) => `jarvis_onboarded_pending:${owner}`;
const recordingOnCore = new Set<string>();
function recordOnCore(owner: TripsOwner): void {
  if (!owner || isDemoSession() || recordingOnCore.has(owner)) return;
  recordingOnCore.add(owner);
  recordOnboardedOnCore(owner)
    .then(
      () => {
        try { localStorage.removeItem(pendingOnCore(owner)); } catch { /* nothing to clear */ }
      },
      (error: unknown) => {
        try { localStorage.setItem(pendingOnCore(owner), "1"); } catch { /* storage unavailable */ }
        reportApiFailure("[onboarding] recording it on core failed", error);
      },
    )
    .finally(() => recordingOnCore.delete(owner));
}
function retryOnCore(owner: TripsOwner, retriedThisLoad: Set<string>): void {
  try {
    if (!owner || retriedThisLoad.has(owner) || localStorage.getItem(pendingOnCore(owner)) !== "1") return;
  } catch {
    return;
  }
  retriedThisLoad.add(owner);
  recordOnCore(owner);
}

// Paths that mean "I do not know where to go yet": the bare root, the loader,
// the auth screens, and onboarding once onboarding is already done. Every other
// path is somewhere the user asked to be — a deep link, a refresh, a shared URL
// — and the routing effect below must not overwrite it (JAR-942).
//
// It used to navigate from EVERY path. A demo user who opened /map/flow landed
// on /trips with nothing to show a redirect had happened, so it read as a bad
// link. It looked correct only because the one environment it was exercised in
// (the capture harness) had no Clerk key, so `isLoaded` never turned true and
// the effect never ran at all.
function isEntryPath(pathname: string): boolean {
  return (
    pathname === "/" ||
    pathname.startsWith("/loader") ||
    pathname.startsWith(AUTH_PREFIX) ||
    pathname === "/onboarding"
  );
}

// The canonical demo identity — shared by demo login and session rehydration so
// a refreshed demo lands back in the same account (JAR-286). The demo user is
// deliberately never written to auth storage; the session flag is the only
// durable trace of a demo.
//
// The email is made up, on a domain kept for examples (RFC 2606), so it reaches
// nobody. It was a real inbox, shown to every demo visitor (JAR-1990).
function makeDemoUser(): User {
  return {
    id: demoSessionId(),
    email: "joe@example.com",
    firstName: "Joe",
    lastName: "User",
    createdAt: new Date().toISOString(),
  };
}

// Analytics knows a demo visit by its session id alone. A profile carrying the
// demo's email would read as a person (JAR-1990).
function identifyDemo(demoUser: User): void {
  identifyUser(demoUser.id, { $first_name: demoUser.firstName });
}

// Main App Content with routing logic
function AppContent() {
  // Track all route changes as Mixpanel page views
  usePageViewTracking();
  // Back lands where the screen was left; a new screen opens at its top (JAR-1480).
  useScrollRestoration();
  const navigate = useNavigate();
  const location = useLocation();
  // A tap on a push while this window shows another screen takes it to the
  // bell. The Dashboard opens the bell in place when it is the screen showing,
  // so this listens only elsewhere (JAR-2389).
  const onDashboard = location.pathname === "/trips" || location.pathname.startsWith("/trips/");
  useEffect(() => {
    const worker = navigator.serviceWorker;
    if (!worker || onDashboard) return;
    const onMessage = (event: MessageEvent) => {
      if ((event.data as { type?: unknown } | null)?.type === OPEN_BELL_MESSAGE) navigate(BELL_PATH);
    };
    worker.addEventListener("message", onMessage);
    worker.startMessages?.();
    return () => worker.removeEventListener("message", onMessage);
  }, [onDashboard, navigate]);
  const { isLoaded, isSignedIn, user: clerkUser } = useUser();
  const clerkStalled = useClerkStalled(isLoaded);
  const { signOut: clerkSignOut, getToken } = useAuth();
  // useSession hands back a pending session, where useAuth reports signed out.
  const pendingTaskPath = taskPath(useSession().session);
  // Whether core marked this invitation link for an address with no account
  // (JAR-2705). Derived here rather than inside the auth effect so the effect
  // can depend on the mark instead of on the whole query string: it reads the
  // mark, not the query, and it also launches checkouts and routes session
  // tasks, which should not re-run because some unrelated parameter changed.
  const invitedNewAddress = isForNewAddress(location.search);
  const [demoUserState, setDemoUserState] = useState<User | null>(null);

  // Derived auth state. The demo is connected to no account (JAR-1846): while
  // its flag is on, the app is the demo identity or nobody, never the signed-in
  // user, so nothing the demo does can read or write under their id.
  const user = useMemo<User | null>(() => {
    if (demoUserState) return demoUserState;
    if (isDemoSession()) return null;
    if (isSignedIn && clerkUser) {
      return {
        id: clerkUser.id,
        email: clerkUser.emailAddresses[0]?.emailAddress || "",
        firstName: clerkUser.firstName || undefined,
        lastName: clerkUser.lastName || undefined,
        createdAt: clerkUser.createdAt?.toISOString() || new Date().toISOString(),
      } as User;
    }
    return null;
  }, [demoUserState, isSignedIn, clerkUser]);

  // The push service can replace this device's subscription while the app is
  // closed, and the worker can't sign in to tell core, so each signed-in start
  // saves it again. Not the demo, which has no account, nor a session that still
  // has a task to finish (JAR-2389).
  const accountId = !demoUserState && !pendingTaskPath ? user?.id ?? null : null;
  useEffect(() => {
    if (accountId) void resaveThisDevice();
  }, [accountId]);

  // Leaving the demo drops its identity with its flag, so the signed-in
  // account, if there is one, comes back.
  const leaveDemo = useCallback(() => {
    clearDemoSession();
    setDemoUserState(null);
    // The demo's journal, scans, flights and preferences would otherwise outlive
    // it and greet the next visitor in this browser (JAR-1992). A signed-in
    // user's own data is keyed elsewhere and is left untouched.
    purgeDemoData();
  }, []);

  // Scope locally-stored trips to this identity before anything reads them
  // (JAR-599). Done during render rather than in an effect on purpose:
  // useDashboardData seeds its state inside useState initialisation, which runs
  // before effects, so a hard load straight to /trips would otherwise take its
  // first read with no owner set.
  //
  // A plain statement, not useMemo: the factory result was thrown away, which
  // is a side effect dressed as a computation. React is free to re-run or drop
  // memos, and StrictMode already double-invoked it. The call is one
  // assignment, so running it every render costs nothing and the timing is
  // unchanged.
  discardLegacyLocalTrips();
  // Settings were global until JAR-834; the pre-namespace key goes the same way
  // as the pre-namespace trips, and at the same chosen moment.
  discardLegacySettings();

  // Route based on auth state once Clerk has loaded.  Guards against
  // redirecting before Clerk is ready (isLoaded=false) to prevent the loader
  // from firing before the session is rehydrated (JAR-441).
  // Pre-Clerk bearer tokens, swept once per load. Only sign-out clears storage,
  // and a browser still holding one from the JWT era is by definition a session
  // that never signed out (JAR-355, low).
  useEffect(() => {
    purgeLegacyAuthKeys();
  }, []);

  // A plan picked on the pricing page waits on the plan gate, selected, for the
  // person to continue to Stripe themselves (JAR-3179). Nothing opens Stripe on
  // its own any more: straight after sign-up or sign-in it used to, with no
  // chance to look at the plans or change the choice. The gate is /onboarding,
  // onboarded or not, and OnboardingFlow drops the choice for someone who is
  // already entitled (JAR-3040).
  //
  // Returns true when it sent them there, so the caller skips its own
  // navigation. A saved link (an invitation) waits for the checkout's return.
  const intentRouted = useRef(false);
  const toPlanGate = useCallback((): boolean => {
    if (!readPlanIntent()) return false;
    intentRouted.current = true;
    navigate("/onboarding", { replace: true });
    return true;
  }, [navigate]);

  // Where the person is now, and who, for a settle whose answer from core lands
  // later.
  const pathNow = useRef(location.pathname);
  useEffect(() => { pathNow.current = location.pathname; }, [location.pathname]);
  const userNow = useRef(user?.id ?? null);
  // The accounts whose failed onboarding write this page load has retried.
  const retriedOnCore = useRef(new Set<string>());
  useEffect(() => { userNow.current = user?.id ?? null; }, [user?.id]);

  useEffect(() => {
    if (!isLoaded) return;
    if (location.pathname.startsWith(SHARED_JOURNAL_PREFIX)) return;
    // Stripe owns this hop. A first-time buyer coming back from checkout is
    // signed in and NOT onboarded by construction (checkout fires before
    // onboarding), so the gate below would eject them to /onboarding the
    // moment Clerk settled and unmount the return screen before its poll
    // could say anything (web-app#252 review, Hermes HIGH). The screen's own
    // Continue lands on /trips, where this effect applies as usual.
    if (location.pathname.startsWith("/checkout/")) return;
    // The MFA screen owns its own exit, for the same reason — and from the first
    // render, not only once enrolment succeeds. isSignedIn here comes from
    // useUser, which reports a PENDING session as signed in (only useAuth treats
    // pending as signed out; see sessionTask.ts). So isSignedIn is already true
    // when the person arrives, and because isEntryPath counts every /auth/ path,
    // settle() would navigate them away before enrolment even starts — and, had
    // it started, before the backup codes were shown. They are shown once. Its
    // Continue lands on "/", where this effect applies as usual (web-app#440
    // review).
    // The demo, before anything about the account underneath it: its MFA
    // screen, its pending tasks and its routing are the account's, and the demo
    // is connected to no account (JAR-1846). The demo identity is set up once,
    // not on every route. An auth screen is left alone, so a demo visitor can
    // still sign in for real. No onboarding gate: the demo is a static example
    // with nobody to onboard, so it reads and writes no onboarding state.
    if (isDemoSession()) {
      if (!location.pathname.startsWith(AUTH_PREFIX)) {
        if (!user?.id) {
          const demoUser = makeDemoUser();
          setDemoUserState(demoUser);
          identifyDemo(demoUser);
        }
        if (isEntryPath(location.pathname)) navigate("/trips", { replace: true });
      }
      return;
    }
    if (location.pathname === SETUP_MFA_PATH) return;
    // A session with a task still to finish goes to the screen that finishes it,
    // from wherever it is. useUser reports it signed in, so typing /trips used to
    // skip enrolment, and core refuses a pending session's requests with 403
    // SESSION_PENDING (JAR-1715).
    if (pendingTaskPath) {
      // An invitation link is answered once the task is done.
      if (isInvitationPath(location.pathname)) saveReturnTo(location.pathname);
      navigate(pendingTaskPath, { replace: true });
      return;
    }
    // Onboarding is a gate, so it applies from wherever the user is. Landing on
    // /trips is only a default for someone who did not ask for a page — hence
    // isEntryPath, rather than redirecting unconditionally.
    //
    // Asking core takes a moment. An answer that arrives after the person has
    // left the path it was asked on, or after someone else has signed in,
    // settles nothing and writes nothing down.
    const settle = (owner: TripsOwner) => {
      const at = location.pathname;
      const decide = (onboarded: boolean) => {
        if (!onboarded) {
          // Not from an invitation link: answering it is how an invitee joins
          // a trip, and a joined trip is what onboards them.
          if (location.pathname !== "/onboarding" && !isInvitationPath(location.pathname)) {
            navigate("/onboarding", { replace: true });
          }
        } else if (isEntryPath(location.pathname) && !(location.pathname === "/onboarding" && readPlanIntent())) {
          // The gate is where a plan picked on the pricing page waits, so an
          // onboarded person holding one is left on it (JAR-3179).
          navigate("/trips", { replace: true });
        }
      };
      retryOnCore(owner, retriedOnCore.current);
      const onboarded = readOnboardedAnywhere(owner);
      if (onboarded === true) return decide(true);
      void onboarded.then((yes) => {
        if (pathNow.current !== at || userNow.current !== owner) return;
        // Core could not be read, so the gate has no answer and stands down:
        // the person stays where they are, and the next load asks again.
        if (yes === "unknown") return;
        if (yes) markOnboarded(owner);
        decide(yes);
      });
    };

    if (isSignedIn && clerkUser) {
      identifyUser(clerkUser.id, {
        $email: clerkUser.emailAddresses[0]?.emailAddress || "",
        $first_name: clerkUser.firstName || "",
        $last_name: clerkUser.lastName || "",
      });
      // The third arrival. A visitor who signs up reaches the gate from
      // handleVerifyEmailDone, and one who signs in from handleSignIn, but a
      // browser that already holds a session sees NO form and reaches neither
      // (JAR-1222). Once a load: someone who leaves the gate is not sent back
      // on every navigation, only on the next load while the choice lasts (an
      // hour, planIntent.ts).
      //
      // Not over an invitation page: it answers as it opens, so the gate waits
      // for the page after it. A decline link drops the plan choice instead.
      if (!intentRouted.current && !isInvitationPath(location.pathname) && toPlanGate()) return;
      settle(user?.id ?? null);
    } else if (location.pathname.startsWith(AUTH_PREFIX)) {
      return;
    } else if (!location.pathname.startsWith("/loader")) {
      // An invitation link needs an account to answer it: resumed after
      // sign-in or sign-up.
      if (isInvitationPath(location.pathname)) {
        saveReturnTo(location.pathname);
        // Core marks the link when the address it mailed the invitation to has
        // no account (JAR-2705). Straight to sign-up, instead of the welcome
        // screen and then sign-in, where someone with no account had to find
        // Sign up for themselves. The screen still offers "Already have an
        // account? Sign in", for someone who signed up after it was sent.
        //
        // Only while self-signup is open. Early Access has it closed (JAR-34)
        // and /auth/sign-up then renders "Invite only for now — join the
        // waitlist", so routing there would tell someone holding a trip
        // invitation to join a waitlist: worse than the screen they get today,
        // and false, because they do have an invite. A closed gate keeps
        // today's path exactly, which is also what an unmarked link does.
        //
        // saveReturnTo above stores the pathname alone, so the mark does not
        // ride back after sign-up. It has done its job by then.
        if (invitedNewAddress && isSelfSignupEnabled()) {
          navigate(SIGN_UP_PATH, { replace: true });
          return;
        }
      }
      navigate("/loader", { replace: true });
    }
  // toPlanGate depends on navigate alone, so listing it cannot re-fire this
  // effect. Listed because the linter is right that an effect reading it
  // should declare it.
  // invitedNewAddress, not location.search: the effect reads the mark, not the
  // query. Depending on the whole string re-ran all of this on any query change
  // — a captured ?plan=, a Plaid return, anything — and this effect also
  // routes plan choices and session tasks. The derived boolean is a
  // primitive, so a re-run happens only when the mark itself flips (AI
  // first-pass, web-app#822).
  }, [isLoaded, isSignedIn, clerkUser, user?.id, location.pathname, invitedNewAddress, navigate, toPlanGate, pendingTaskPath]);

  // A Trip Pass trip that has ended refuses its planning with a sentence; one
  // toast says it, over whichever screen made the write (JAR-1860).
  useTripLockedToast();

  // Listen for auth expiration events — Clerk handles session expiry, but
  // the old custom event may still fire from api/client.ts (JAR-396).
  useEffect(() => {
    const handleExpired = () => {
      // A 401 can reach here while Clerk still holds a live session — a token that
      // expired in flight, or a core clock a few seconds behind Clerk's. Purging
      // then wiped a still-signed-in user's browser-kept data (trips not yet
      // saved, Explore hearts, dining style, receipt scans, onboarding flags) for
      // a problem a retry or core leeway covers, and the routing bounced them
      // back to /trips still signed in but empty (JAR-2268). So confirm the
      // session is really gone before touching anything.
      void (async () => {
        let stillLive = false;
        try {
          stillLive = (await getToken({ skipCache: true })) != null;
        } catch {
          // No usable session is exactly the case we purge for; a throw is not a
          // reason to keep stale data readable by the next person on this browser.
          stillLive = false;
        }
        // Clerk still has a session: not a real expiry. Leave the live session and
        // its data alone — do not purge, do not route away.
        if (stillLive) return;

        // Really gone. Expiry never runs handleSignOut, so without this the
        // trips and receipt scans of the expired session stay readable by whoever
        // signs in next on this browser. Sign Clerk out too, so the app is in a
        // genuinely signed-out state rather than signed-in with nothing.
        await purgeAllLocalUserData();
        try {
          await clerkSignOut();
        } catch {
          // Even if the sign-out call fails, the data is already purged and we
          // still route to sign-in; a lingering Clerk session will re-resolve.
        }
        navigate(SIGN_IN_PATH, { replace: true });
      })();
    };
    window.addEventListener("auth:expired", handleExpired);
    return () => window.removeEventListener("auth:expired", handleExpired);
  }, [navigate, getToken, clerkSignOut]);

  // Global safety net — anything that slips past local try/catch (a stray
  // rejected promise, a runtime error) gets logged so it's never silent, and an
  // unhandled async failure surfaces a calm toast instead of failing invisibly.
  useEffect(() => {
    const onRejection = (e: PromiseRejectionEvent) => {
      captureException(e.reason, { source: "unhandledrejection" });
      toast.error("Something went wrong. Please try again.");
    };
    const onError = (e: ErrorEvent) => captureException(e.error ?? e.message, { source: "window.onerror" });
    window.addEventListener("unhandledrejection", onRejection);
    window.addEventListener("error", onError);
    return () => {
      window.removeEventListener("unhandledrejection", onRejection);
      window.removeEventListener("error", onError);
    };
  }, []);

  // Handle loader completion
  const handleLoaderComplete = useCallback(() => {
    // Clerk handles session — if we reached here without a user, go to sign-in.
    // The mount effect handles Clerk rehydration; this is a fallback.
    navigate(SIGN_IN_PATH, { replace: true });
  }, [navigate]);

  // Handle sign in — Clerk activated the session in SignInScreen.
  // A plan picked on the pricing page first, then a saved link, then route based on onboarding state.
  const handleSignIn = async () => {
    // Signing in for real ends a demo this browser was in.
    leaveDemo();
    try {
      const uid = clerkUser?.id || "";
      const uemail = clerkUser?.emailAddresses[0]?.emailAddress || "";
      if (uid) identifyUser(uid, { $email: uemail });
      trackLogin("email");
      // A pricing-page choice is honoured on THIS path too, not only after
      // email verification: an existing account that arrives at /?plan=... and
      // signs in had the intent captured and then never acted on, lingering in
      // sessionStorage until the tab closed (OCR MED, web-app#252). The gate
      // outranks a remembered deep link, which waits for the checkout's return.
      if (toPlanGate()) return;
      const returnTo = consumeReturnTo();
      if (returnTo) {
        navigate(returnTo, { replace: true });
      } else {
        // Signing in has to land somewhere, so an unreadable core lands on the
        // trips list rather than onboarding: it is where someone with trips on
        // core belongs, and the gate asks again from there.
        const answer = readOnboardedAnywhere(uid || null);
        const onboarded = answer === true ? true : await answer;
        if (onboarded === false) {
          navigate("/onboarding", { replace: true });
        } else {
          // The browser's own yes stays in the browser: its flag may be another
          // person's. Core's yes is this account's, and core keeps it, unless
          // someone else is signed in by the time it arrives, as the gate checks.
          if (answer !== true && onboarded === true && userNow.current === (uid || null)) markOnboarded(uid || null);
          navigate("/trips", { replace: true });
        }
      }
    } catch (err) {
      captureException(err, { source: "loginPostAuth" });
    }
  };

  // Into the demo, from the sign-in page and from the onboarding plan gate
  // alike. The demo is a static example connected to no account (Brent,
  // JAR-1846): it runs as the demo identity, and writes nothing the next
  // session in this browser reads. No onboarding flag, and no first-run tour,
  // whose seen state the browser shares (JAR-1850). The demo banner's "Choose a
  // plan" returns a signed-in person to their account as they left it.
  const startDemo = useCallback(() => {
    // Start clean, even if a previous demo's leave never ran (a crash, a killed
    // tab): the next visitor must not inherit the last one's demo data (JAR-1992).
    purgeDemoData();
    setDemoSession();
    const demoUser = makeDemoUser();
    setDemoUserState(demoUser);
    identifyDemo(demoUser);
    trackLogin("demo");
    navigate("/trips", { replace: true });
  }, [navigate]);

  // "Choose a plan" from the demo banner — leave the demo and return to the
  // plan gate to pick Trip Pass / Explore.
  const handleExitDemoToPlans = useCallback(() => {
    // One transition with the navigation. react-router applies navigate() as a
    // transition, so an urgent identity change would render first and mount
    // the account's dashboard on the demo's page for a frame.
    startTransition(() => {
      leaveDemo();
      navigate("/onboarding", { replace: true });
    });
  }, [navigate, leaveDemo]);

  // Marketing links in as app.jarvistravel.com/?plan=jt_explore_annual. Capture
  // that before anything else runs. A LAYOUT effect, not a passive one: the `/`
  // route renders <Navigate to="/loader" replace />, and React flushes a child's
  // passive effects before its parent's, so Navigate's replaceState had already
  // stripped the query string by the time a passive effect here read it — on
  // the primary entry path (web-app#252 review, Hermes HIGH). A parent's layout
  // effects run before any child's passive effects in the same commit. Empty
  // deps so it reads the URL the visitor actually arrived on, exactly once.
  useLayoutEffect(() => {
    capturePlanIntent();
  }, []);



  // Handle sign up — Clerk created the user in SignUpScreen.
  const handleSignUp = async (_data: { email: string; firstName: string; lastName?: string; phone?: string; password: string }) => {
    leaveDemo();
    try {
      const uid = clerkUser?.id || "";
      const uemail = clerkUser?.emailAddresses[0]?.emailAddress || "";
      if (uid) identifyUser(uid, { $email: uemail });
      trackSignUp("email");
      navigate(VERIFY_EMAIL_PATH, { replace: true });
    } catch (err) {
      captureException(err, { source: "registrationPostAuth" });
    }
  };

  // Email verified (or skipped) → the plan gate if they came with a plan
  // picked, else the link that sent them to sign up (an invitation), else
  // onboarding. The link is read only when it is followed, so it survives the
  // gate and the checkout after it.
  const handleVerifyEmailDone = useCallback(() => {
    if (toPlanGate()) return;
    navigate(consumeReturnTo() ?? "/onboarding", { replace: true });
  }, [navigate, toPlanGate]);

  // A session task is done → the plan gate if a plan was picked, as after
  // sign-in, with the link waiting for the checkout's return; else the link
  // that needed an account, if one is waiting (an invitation), else "/", where
  // the routing effect applies as usual.
  const handleTaskDone = useCallback(() => {
    if (toPlanGate()) return;
    navigate(consumeReturnTo() ?? "/", { replace: true });
  }, [navigate, toPlanGate]);

  // The gate dropped a plan picked on the pricing page, because the person
  // already holds an entitlement (JAR-3040): into the app, by the link that
  // brought them if one is waiting. Someone not yet onboarded is sent on from
  // /trips to onboarding by the routing effect, as after a checkout.
  const handlePlanIntentDropped = useCallback(() => {
    navigate(consumeReturnTo() ?? "/trips", { replace: true });
  }, [navigate]);

  // Jarvis built and saved a trip — mark onboarding done + open it.
  const handlePlannerBuilt = useCallback((trip: JournalTrip) => {
    markOnboarded(userNow.current);
    trackTripCreated("onboarding");
    // Their first trip just appeared — queue the tour that walks them around it.
    setPendingTour('trip');
    navigate(`/trips/${trip.id}`, { replace: true });
  }, [navigate]);

  // Handle sign out — clear Clerk session, demo state, analytics, and every
  // key this app has written to browser storage.
  const handleSignOut = useCallback(async () => {
    clearDemoSession();
    setDemoUserState(null);
    resetAnalytics();
    trackLogout();
    // Push is per device, and goes with the session, so the next person at this
    // browser doesn't get this account's pushes. Not awaited: the browser dropping
    // the subscription is what stops them, and core's delete is cleanup that a
    // slow network shouldn't hold sign-out for.
    void turnOffPush();
    // Before Clerk's teardown, deliberately. That teardown can reject (offline,
    // a revoked session), and the purge must not be skipped when it does — that
    // is the case where the next person at this browser is most likely to be
    // someone else. Awaited so the API-cache drop completes before we hand the
    // browser to the sign-in screen. purgeAllLocalUserData never throws.
    await purgeAllLocalUserData();
    try { await clerkSignOut(); } catch { /* best effort */ }
    navigate(SIGN_IN_PATH, { replace: true });
  }, [navigate, clerkSignOut]);

  // Signed in as someone an invitation wasn't sent to: sign out as Settings
  // does, and keep the link for the account it was. Saved after the purge,
  // which clears it with the rest of this browser's data.
  const handleSwitchAccount = useCallback(async (path: string) => {
    await handleSignOut();
    saveReturnTo(path);
  }, [handleSignOut]);

  // Back from checkout → the link that needed a plan, if one is waiting (an
  // invitation), else the trips list.
  const handleCheckoutContinue = useCallback(() => {
    navigate(consumeReturnTo() ?? "/trips", { replace: true });
  }, [navigate]);

  // Reflect a saved profile (from Settings) in the app's user state.
  //
  // Demo patches local state only — it is connected to no account (JAR-1846),
  // so it must never write to a real Clerk user.
  //
  // Live, this is the half that was missing (JAR-2258). The `user` memo above
  // builds the signed-in identity out of `clerkUser`, so a name written only to
  // core surfaced nowhere: Settings and the greeting kept rendering "Traveler"
  // even without a reload, because setDemoUserState is a no-op when signed in.
  // Writing it to Clerk here re-renders useUser, and the memo recomputes.
  const handleProfileUpdated = useCallback(async (patch: { firstName?: string; lastName?: string; phone?: string; email?: string }) => {
    setDemoUserState((prev) => (prev ? { ...prev, ...patch } : prev));
    if (isDemoSession()) return;
    await syncNameToClerk(clerkUser, { firstName: patch.firstName, lastName: patch.lastName });
  }, [clerkUser]);

  // Handle forgot password complete
  const handleForgotPasswordComplete = useCallback(() => {
    navigate(SIGN_IN_PATH, { replace: true });
  }, [navigate]);

  // A page that needs the account waits for Clerk to say who it is. When Clerk
  // can't be reached, it says so, rather than "Loading…" with no way on (JAR-2493).
  const accountFallback = clerkStalled ? (
    <div className="fixed inset-0 flex items-center justify-center px-6">
      <SignInUnreachable />
    </div>
  ) : appFallback;

  // Both dashboard routes (/trips and /trips/:tripId) render this one element,
  // so the identity gate and the Dashboard's props can't drift apart.
  //
  // Mounted only once there is an identity. Before, the routes rendered with
  // user = null: a signed-out visitor saw the dashboard ("Hi, Traveler") for a
  // moment and it fired nine authenticated calls before the boot effect sent
  // them to sign-in; a signed-in hard load mounted the same anonymous dashboard
  // while Clerk hydrated, then remounted it by key once identity arrived
  // (JAR-1401). Gating on `user` means the dashboard's first mount already has
  // its owner, so useDashboardData seeds from the right trips the first time —
  // the JAR-599 remount is no longer needed to correct it. The key stays, so
  // switching accounts still remounts cleanly.
  const dashboardElement = user ? (
    <Suspense fallback={appFallback}>
      <Dashboard
        key={user.id}
        owner={user.id}
        user={{
          name: user.firstName || "Traveler",
          email: user.email || "",
          phone: user.phone,
        }}
        onSignOut={handleSignOut}
        onProfileUpdated={handleProfileUpdated}
        onExitDemo={handleExitDemoToPlans}
      />
    </Suspense>
  ) : accountFallback;

  // The same gated block was written out five times, differing only in the screen
  // (JAR-1494). Same idiom as dashboardElement above: one value built from `user`,
  // so the gate, the fallback, the key and the owner prop are stated once and
  // cannot drift apart.
  //
  // THE KEY IS LOAD-BEARING, not decoration. Switching accounts must remount the
  // screen rather than hand the next owner the previous one's state, and that is
  // what "remounts the story fresh when the signed-in account changes" pins. It
  // pins it for all five only because /journal/:tripId goes through here too: the
  // four map routes have no remount test of their own, so a version of this helper
  // covering only the maps would leave the key guarded by nothing.
  //
  // /map/nearby is deliberately NOT one of these. It takes no owner and is not
  // gated, which is right for a search that needs no trip, and folding it in would
  // change behaviour rather than tidy it.
  const gatedScreen = (
    Screen: ComponentType<{ owner: TripsOwner }>,
    fallback: ReactNode = mapFallback,
  ) =>
    user ? (
      <Suspense fallback={fallback}>
        <Screen key={user.id} owner={user.id} />
      </Suspense>
    ) : accountFallback;

  return (
    <div className="font-sans antialiased bg-bg text-ink min-h-screen transition-colors">
      {/* Transient notifications (errors, confirmations) — rendered once, app-wide */}
      <Toaster />

      {/* App-wide "can't reach the backend" status strip — shows on any screen
          (incl. auth), auto-dismisses when the backend answers again (JAR-314).
          ORDER IS LOAD-BEARING: the backend strip is mounted before the update
          strip so, when both show, it sits above — an unreachable backend matters
          more. The z-indexes are gone (JAR-2497), so document order is the only
          thing carrying this. App.test.tsx pins it on this tree: swap the two
          and it fails (JAR-3378). */}
      <BackendUnavailableBanner />

      {/* A deploy has reached this open app: offer the new build (JAR-2323).
          Second of the two app-root strips — see the ordering note above. */}
      <UpdateReadyBanner />

      {/* Post-login "Add to Home Screen" nudge, OFF for launch behind
          SHOW_ADD_TO_HOME_SCREEN_NUDGE (JAR-2464): the installed PWA is not good
          enough yet, so we do not send anyone to install it until JAR-2463's
          audit. When re-enabled: real signed-in users only (not demo), on
          phones, at the 1st/3rd/6th activation (the component renders nothing
          otherwise), and not during onboarding, so it waits until they reach the
          app rather than stacking over the first-run tour. Keyed by identity so
          a fresh sign-in re-arms it, since sign-out's purge clears the
          per-session guard (JAR-2031). */}
      {SHOW_ADD_TO_HOME_SCREEN_NUDGE &&
        isSignedIn && clerkUser && !demoUserState && !location.pathname.startsWith("/onboarding") && (
          <AddToHomeScreenPrompt key={clerkUser.id} />
      )}

      {/* Per-route boundary: a crash in one screen shows a recover card (not a
          blank app); navigating away clears it via resetKeys. */}
      <ErrorBoundary
        resetKeys={[location.pathname]}
        fallback={(reset) => (
          <RouteErrorFallback
            onRetry={reset}
            onHome={() => {
              navigate("/trips", { replace: true });
              reset();
            }}
          />
        )}
      >
      <Routes>
        {/* Loader route */}
        <Route
          path="/loader"
          element={<LoaderScreen onComplete={handleLoaderComplete} />}
        />

        {/* Where an OAuth bank returns the user (JAR-1173). The path is fixed:
            it must equal the URI registered in the Plaid dashboard and the
            redirect_uri the link token carries, or Plaid refuses the token.
            Not an entry path, so the auth effect above leaves it alone — a
            redirect here would drop the user mid-link with no way back. */}
        <Route path="/plaid-oauth-return" element={
          <Suspense fallback={appFallback}>
            <PlaidOAuthReturn />
          </Suspense>
        } />

        {/* Auth routes */}
        <Route
          path={SIGN_IN_PATH}
          element={
            <SignInScreen
              onSessionTask={(path) => navigate(path, { replace: true })}
              onSignIn={handleSignIn}
              onSignUp={() => navigate(SIGN_UP_PATH)}
              onForgotPassword={() => navigate(FORGOT_PASSWORD_PATH)}
              onVerifyEmail={() => navigate(VERIFY_EMAIL_PATH)}
              onDemoLogin={startDemo}
            />
          }
        />
        {/* Self-signup is closed during Early Access (JAR-34) — the route stays
            mounted so existing links and bookmarks land somewhere useful
            instead of 404-ing. */}
        <Route
          path={SIGN_UP_PATH}
          element={
            isSelfSignupEnabled() ? (
              <SignUpScreen
                onSignUp={handleSignUp}
                onSignIn={() => navigate(SIGN_IN_PATH)}
              />
            ) : (
              <EarlyAccessNotice onSignIn={() => navigate(SIGN_IN_PATH)} />
            )
          }
        />
        <Route
          path={FORGOT_PASSWORD_PATH}
          element={
            <ForgotPasswordScreen
              onSessionTask={(path) => navigate(path, { replace: true })}
              onBack={() => navigate(SIGN_IN_PATH)}
              onComplete={handleForgotPasswordComplete}
            />
          }
        />
        {/* Clerk requires a second factor on the production instance, so a new
            account's session arrives PENDING with a setup-mfa task that only
            this screen finishes. Landing on "/" afterwards lets the signed-in
            effect settle them: onboarding for a new account, their trips for
            someone who already has one (JAR-1700). */}
        <Route
          path={SETUP_MFA_PATH}
          element={<SetupMfaScreen onDone={handleTaskDone} onSignOut={handleSignOut} />}
        />
        <Route
          path={VERIFY_EMAIL_PATH}
          element={
            <VerifyEmailScreen
              onSessionTask={(path) => navigate(path, { replace: true })}
              email={user?.email || ""}
              onVerified={handleVerifyEmailDone}
              onSkip={handleVerifyEmailDone}
              onSignIn={() => navigate(SIGN_IN_PATH)}
            />
          }
        />



        {/* Onboarding route — Intro → Jarvis planner → built trip */}
        <Route
          path="/onboarding"
          element={
            <Suspense fallback={appFallback}>
              <OnboardingFlow
                owner={user?.id ?? null}
                userName={user?.firstName || "there"}
                onComplete={handlePlannerBuilt}
                onSkip={() => { markOnboarded(user?.id ?? null); setPendingTour('explore'); navigate("/trips", { replace: true }); }}
                onTryDemo={startDemo}
                onPlanIntentDropped={handlePlanIntentDropped}
                onSignOut={handleSignOut}
              />
            </Suspense>
          }
        />

        {/* Dashboard routes: both render the one gated element (dashboardElement, above). */}
        <Route path="/trips" element={dashboardElement} />
        <Route path="/trips/:tripId" element={dashboardElement} />
        <Route path={INVITATION_PATHS.accept} element={<InvitationLink answer="accept" onSwitchAccount={handleSwitchAccount} />} />
        <Route path={INVITATION_PATHS.decline} element={<InvitationLink answer="decline" onSwitchAccount={handleSwitchAccount} />} />
        {/* Settings is a tab inside the dashboard, held in history state rather than
            the URL, so no URL could reach it — and a return URL is exactly what
            Stripe's billing portal needs. Manage billing handed Stripe the page's own
            href, which carries no tab, so a customer came back to Home instead of the
            Settings they left (JAR-1698). This names the tab the only way the
            dashboard reads one. */}
        <Route path={SETTINGS_PATH} element={<Navigate to="/trips" replace state={{ tab: 'settings' }} />} />
        <Route path={BELL_PATH} element={<Navigate to="/trips" replace state={{ openBell: true }} />} />

        {/* Map — Trip Overview (M1). The trip maps are gated and keyed like the journal
            story: each draws one of the signed-in user's trips, named in its URL (JAR-1435). */}
        <Route path="/map" element={gatedScreen(TripOverviewMapScreen)} />

        {/* Map — Nearby search (M3) */}
        <Route
          path="/map/nearby"
          element={
            <Suspense fallback={mapFallback}>
              <NearbySearchMapScreen />
            </Suspense>
          }
        />

        {/* Map — Itinerary item context (M3) */}
        <Route path="/map/item" element={gatedScreen(ItineraryItemMapScreen)} />

        {/* Map — Progressive Flow hero (M4) */}
        <Route path="/map/flow" element={gatedScreen(ProgressiveFlowMapScreen)} />

        {/* Map — Journal memory map (M5) */}
        <Route path="/map/journal/:tripId?" element={gatedScreen(JournalMemoryMapScreen)} />

        {/* Trip Journal — the story for one trip (J2). Gated like the Dashboard: the
            story looks for the trip among the signed-in user's own, and it's keyed on
            the user like the Dashboard, so another account gets a fresh mount (JAR-1434). */}
        <Route
          path="/journal/:tripId"
          element={gatedScreen(
            TripJournalStory,
            <div className="fixed inset-0 flex items-center justify-center text-muted">Loading journal…</div>,
          )}
        />

        {/* Public, read-only shared journal (J4) — no account needed */}
        <Route
          path={SHARED_JOURNAL_ROUTE}
          element={
            <Suspense fallback={<div className="fixed inset-0 flex items-center justify-center text-muted">Loading journal…</div>}>
              <SharedJournalRoute />
            </Suspense>
          }
        />

        {/* Root redirect */}
        {/* Where Stripe sends the buyer back. Both paths were already named by
            redirectToCheckout and SubscriptionManager before these routes
            existed, so they resolved through the catch-all and dropped the
            buyer into /trips with no confirmation that anything had happened. */}
        <Route
          path={CHECKOUT_RETURN_PATHS.success}
          element={<CheckoutReturn outcome="success" onContinue={handleCheckoutContinue} />}
        />
        <Route
          path={CHECKOUT_RETURN_PATHS.canceled}
          element={<CheckoutReturn outcome="canceled" onContinue={handleCheckoutContinue} />}
        />

        <Route path="/" element={<Navigate to="/loader" replace />} />

        {/* Catch-all: redirect based on auth state */}
        <Route
          path="*"
          element={
            user ? (
              <Navigate to="/trips" replace />
            ) : (
              <Navigate to={SIGN_IN_PATH} replace />
            )
          }
        />
      </Routes>
      </ErrorBoundary>
    </div>
  );
}

// Root App
export default function App() {
  // The app-wide boundary is the last one anything falls back to, and without
  // resetKeys it never cleared: one throw anywhere, and every later route
  // rendered the crash screen. The nav still worked and the path still changed —
  // only a reload brought the app back (JAR-2749).
  //
  // Keyed on the pathname, like the per-route boundary above. A route change is
  // the one signal that says the visitor has left whatever broke; the boundary
  // clears, the new route mounts, and it catches again if that route throws too.
  // If the same error comes straight back, it will keep coming back wherever the
  // visitor goes, so the screen holds and says so instead (JAR-2755).
  //
  // THIS MUST RENDER INSIDE A ROUTER. AppContent has always needed one (it calls
  // useLocation and useNavigate itself), so the requirement is not new — but it
  // used to sit BELOW this boundary, where an out-of-router render surfaced as
  // the crash screen. Reading the location out here puts it ABOVE the boundary,
  // so the same mistake now fails the render outright instead of being caught
  // and shown as "something went wrong". Louder, and a crash screen was never a
  // useful answer to a wiring mistake. The one render site is AppRoutes.tsx,
  // inside BrowserRouter; tests wrap it in MemoryRouter.
  const { pathname } = useLocation();
  return (
    <ErrorBoundary resetKeys={[pathname]}>
      <AppContent />
    </ErrorBoundary>
  );
}
